'use client';

import {
  RegisterFormInputs,
  useRegisterForm,
} from '@web-libs/features/auth/register/application/register-form.concern';
import {
  getTextFieldColor,
  TextField,
  TextFieldColor,
} from '@web-libs/core/forms/ui/fields/text-field/text-field';
import {
  Checkbox,
  Dropdown,
} from '@web-libs/core/forms/ui/fields/checkbox/checkbox';
import { FormLayout } from '@web-libs/core/forms/ui/layout/form-layout';
import { EnhancedPasswordField } from '@web-libs/core/forms/ui/fields/enhanced-password/enhanced-password-field';
import { FormButton } from '@web-libs/core/forms/ui/button/form-button';
import { UseTageaFormRegisterReturn } from '@web-libs/core/forms/application/use-tagea-form.types';
import { UseFormTrigger } from 'react-hook-form';
import { useDictionaryContext } from '@web-libs/core/internationalization/dictionary.context';
import React from 'react';
import Link from 'next/link';
import { useTageaRouter } from '@web-libs/core/routing/application/use-tagea-router.concern';
import { useToaster } from '@web-libs/features/toaster/use-toaster';

function getField<T extends keyof RegisterFormInputs>(
  field: UseTageaFormRegisterReturn<T> | undefined,
  triggerValidation: UseFormTrigger<RegisterFormInputs>,
) {
  if (field) {
    const { onChange, ...restOfField } = field;
    const newOnChange = async (event: { target: any; type?: any }) => {
      const changeEvent = await onChange(event);
      await triggerValidation(field.name);
      return changeEvent;
    };
    return {
      onChange: newOnChange,
      ...restOfField,
    };
  } else {
    return undefined;
  }
}

export const RegisterForm = ({
  onSuccess,
  foregroundColor,
  enableAnonymous,
}: {
  onSuccess: () => void;
  foregroundColor?: TextFieldColor;
  enableAnonymous: boolean;
}) => {
  const dict = useDictionaryContext();
  const toaster = useToaster();

  const handleSuccessWithToast = () => {
    toaster.success(dict.registration.forwarding.title);
    onSuccess();
  };

  const {
    fields,
    handleSubmit,
    serverError,
    trigger,
    specializations,
    isSubmitting,
  } = useRegisterForm(handleSuccessWithToast);

  const userNameFieldRegister = getField(fields.userName, trigger);
  const emailFieldRegister = getField(fields.email, trigger);

  const router = useTageaRouter();

  foregroundColor ??= 'lightOnPrimary';

  const checkboxColor = getTextFieldColor(foregroundColor);

  const firstNameField = fields.firstName ? (
    <TextField color={foregroundColor} fitParent {...fields.firstName} />
  ) : null;
  const lastNameField = fields.lastName ? (
    <TextField color={foregroundColor} fitParent {...fields.lastName} />
  ) : null;
  const emailField = emailFieldRegister ? (
    <TextField color={foregroundColor} fitParent {...emailFieldRegister} />
  ) : null;
  const passwordField = fields.password ? (
    <EnhancedPasswordField
      color={foregroundColor}
      fitParent
      showRepeatPassword={true}
      showStrengthIndicator={true}
      showToggle={true}
      {...fields.password}
    />
  ) : null;
  const passwordConfirmationField = null; // Enhanced password field handles repeat password internally
  const usernameField = userNameFieldRegister ? (
    <TextField color={foregroundColor} fitParent {...userNameFieldRegister} />
  ) : null;
  const anonymousCheckbox = fields.anonymous ? (
    <Checkbox color={checkboxColor} {...fields.anonymous} />
  ) : null;
  const birthdayField = fields.birth ? (
    <TextField color={foregroundColor} fitParent {...fields.birth} />
  ) : null;
  const mobileField = fields.mobile ? (
    <TextField
      type={'tel'}
      color={foregroundColor}
      fitParent
      {...fields.mobile}
    />
  ) : null;

  const specializationPlaceholder =
    dict.registration.specialization_please_select;
  const specializationNoSelection =
    dict.registration.specialization_no_selection;
  const specializationOptions = specializations.map((specialization) => {
    return { value: specialization.id, label: specialization.name };
  });
  const specializationField = fields.specializationId ? (
    <Dropdown
      noSelectionLabel={specializationNoSelection}
      placeholder={specializationPlaceholder}
      style={{ marginTop: 16 }}
      color={getTextFieldColor(foregroundColor)}
      options={specializationOptions}
      {...fields.specializationId}
    />
  ) : null;

  const privacyPolicyText = dict.registration.privacyPolicy;
  const split = privacyPolicyText.split('%s');

  const privacyPolicyCheckbox = fields.privacyPolicy ? (
    <Checkbox
      style={{ marginTop: 20 }}
      color={checkboxColor}
      {...fields.privacyPolicy}
    >
      {split[0]}{' '}
      <Link
        href={router.getLink('/datenschutz')}
        style={{ textDecoration: 'underline' }}
      >
        {dict.registration.privacyPolicyLink}
      </Link>{' '}
      {split[1]}
    </Checkbox>
  ) : null;

  const fieldsArray = [];
  if (anonymousCheckbox && enableAnonymous) {
    fieldsArray.push([anonymousCheckbox]);
  }
  if (emailField) {
    fieldsArray.push([emailField]);
  }
  if (firstNameField && lastNameField) {
    fieldsArray.push([firstNameField, lastNameField]);
  }
  if (birthdayField) {
    fieldsArray.push([birthdayField]);
  }

  if (mobileField) {
    fieldsArray.push([mobileField]);
  }

  if (usernameField) {
    fieldsArray.push([usernameField]);
  }

  if (passwordField) {
    fieldsArray.push([passwordField]);
  }
  // passwordConfirmationField is handled by enhanced password input
  if (specializationField) {
    fieldsArray.push([specializationField]);
  }
  if (privacyPolicyCheckbox) {
    fieldsArray.push([privacyPolicyCheckbox]);
  }

  const button = (
    <FormButton
      error={serverError}
      color={'light'}
      text={dict.register}
      onClick={handleSubmit}
      loading={isSubmitting}
    ></FormButton>
  );

  return <FormLayout fields={fieldsArray} button={button} />;
};
